/** TanStack Query hooks for course draft publishing and quick-fix. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { courseKeys } from './useCourses';
import { courseVersionKeys } from './useCourseVersions';
import { lessonKeys } from './useLessons';
import type { ConflictReport, ConflictResolution, QuickFixStepInput } from '@formency/shared';
import { isAxiosError } from 'axios';

// ─── Publish ──────────────────────────────────────────────────────────────────

/**
 * Discriminated union for publish results.
 * 409 conflicts are caught and returned as data (not thrown) so the UI can
 * show a resolution dialog without entering the mutation's error state.
 * Consumers discriminate via `'published' in result` or `'conflicts' in result`.
 */
export type PublishDraftResult = { published: true } | { conflicts: ConflictReport };

/** Publish a course draft. Returns conflict report on 409. */
export function usePublishDraft(courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      versionId,
      resolutions,
    }: {
      versionId: string;
      resolutions?: ConflictResolution[];
    }): Promise<PublishDraftResult> => {
      try {
        await api.post(`/courses/${courseId}/versions/${versionId}/publish`, { resolutions });
        return { published: true as const };
      } catch (error: unknown) {
        if (isAxiosError(error) && error.response?.status === 409) {
          return { conflicts: error.response.data.data as ConflictReport };
        }
        throw error;
      }
    },
    onSuccess: (result) => {
      if ('published' in result) {
        qc.invalidateQueries({ queryKey: courseVersionKeys.all(courseId) });
        qc.invalidateQueries({ queryKey: courseKeys.content(courseId) });
        qc.invalidateQueries({ queryKey: ['f', 'courses'] });
        qc.invalidateQueries({ queryKey: ['f', 'lessons'] });
        // Invalidate individual lesson-with-steps caches so step editors
        // reflect freshly published content (e.g. new live version pointers).
        qc.invalidateQueries({ queryKey: lessonKeys.all });
      }
    },
  });
}

// ─── Merge Check ──────────────────────────────────────────────────────────────

/** Dry-run merge detection. */
export function useMergeCheck(courseId: string, versionId: string | undefined) {
  return useQuery<ConflictReport>({
    queryKey: [...courseVersionKeys.detail(courseId, versionId!), 'merge-check'],
    queryFn: () =>
      api.get<ConflictReport>(`/courses/${courseId}/versions/${versionId}/merge-check`),
    enabled: !!courseId && !!versionId,
    staleTime: 0, // Always fresh
  });
}

// ─── Quick Fix ────────────────────────────────────────────────────────────────

/** Apply a quick-fix to a single step. */
export function useQuickFixStep(lessonId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stepId, ...body }: { stepId: string } & QuickFixStepInput) =>
      api.post(`/lessons/${lessonId}/steps/${stepId}/quick-fix`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: lessonKeys.withSteps(lessonId) });
    },
  });
}
